// RouteDetailsModal.jsx
import React, { useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import MapComponent from '../../Logist/LogisistMainPage/components/LogistMapComponent';
import styles from './RouteDetailsModal.module.css';

const STATUS_COLOR = {
  Закрыта: '#28a745',
  'Не доставлено': '#dc3545',
  Открыта: '#007bff',
};

const NO_CHECK_TYPES = ['ПоСчету', 'Кредит', 'Взаимозачет', 'Дилер', 'БезОплаты'];

export default function RouteDetailsModal({ isOpen, onClose, route }) {
  // Хуки — ВСЕГДА первые и без условий
  const [showUnscannedDetails, setShowUnscannedDetails] = useState(false);
  const [timelineMode, setTimelineMode] = useState('status'); // 'status' | 'scan'
  const [highlightReceiptId, setHighlightReceiptId] = useState(null);

  // Безусловно вычисляем "open", а не ранний return — это устраняет предупреждение ESLint
  const open = Boolean(isOpen && route);

  // Безопасные данные
  const receipts = useMemo(
    () => (Array.isArray(route?.receipts) ? route.receipts : []),
    [route]
  );

  // Вспомогалки
  const fmtDate = (dt) =>
    dt instanceof Date && !isNaN(dt)
      ? dt.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      : '—';

  const fmtTime = (dt) =>
    dt instanceof Date && !isNaN(dt)
      ? dt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      : '—';

  // Первая/последняя метка изменения статуса
  const { firstChangeTime, lastChangeTime } = useMemo(() => {
    const times = receipts
      .map((r) => r?.status_changed_at)
      .filter(Boolean)
      .map((d) => new Date(d).getTime())
      .filter((n) => Number.isFinite(n));
    if (!times.length) return { firstChangeTime: null, lastChangeTime: null };
    return {
      firstChangeTime: new Date(Math.min(...times)),
      lastChangeTime: new Date(Math.max(...times)),
    };
  }, [receipts]);

  // Детализация недосканированных
  const unscannedDetails = useMemo(() => {
    const acc = [];
    for (const r of receipts) {
      if (r?.status === 'Не доставлено') continue;
      const prods = Array.isArray(r?.products) ? r.products : [];
      for (const p of prods) {
        if (!p?.is_marked) continue;
        const name = p?.product_name || '—';
        const qty = Number(p?.actual_quantity ?? 0) || 0;
        const scanned = Array.isArray(p?.marks) ? p.marks.length : 0;
        const missing = Math.max(qty - scanned, 0);

        const exist = acc.find((x) => x.name === name);
        if (!exist) acc.push({ name, total: qty, scanned, missing });
        else {
          exist.total += qty;
          exist.scanned += scanned;
          exist.missing += missing;
        }
      }
    }
    return acc;
  }, [receipts]);

  // Общая статистика
  const stats = useMemo(() => {
    const total = receipts.length;
    const inProgress = receipts.filter((r) => r?.status === 'Открыта').length;
    const closed = receipts.filter((r) => r?.status === 'Закрыта').length;
    const undelivered = receipts.filter((r) => r?.status === 'Не доставлено').length;
    const unscannedItems = unscannedDetails.reduce((s, x) => s + x.missing, 0);
    const notChecked = receipts.filter((r) => {
      if (r?.status === 'Не доставлено') return false;
      const needsCheck = !NO_CHECK_TYPES.includes(r?.payment_type);
      return needsCheck && !r?.is_check_issued;
    }).length;

    return { total, inProgress, closed, undelivered, unscannedItems, notChecked };
  }, [receipts, unscannedDetails]);

  // Проблемные квитанции
  const problems = useMemo(() => {
    return receipts.filter((r) => {
      if (!r) return false;
      if (r.status === 'Не доставлено') return false;
      if (r.status === 'Открыта') return true;

      const prods = Array.isArray(r.products) ? r.products : [];
      const marked = prods.filter((p) => p?.is_marked);
      const totalQty = marked.reduce((s, p) => s + (Number(p?.actual_quantity ?? 0) || 0), 0);
      const scannedQty = marked.reduce((s, p) => s + (Array.isArray(p?.marks) ? p.marks.length : 0), 0);
      if (scannedQty < totalQty) return true;

      const needsCheck = !NO_CHECK_TYPES.includes(r.payment_type);
      if (needsCheck && !r.is_check_issued) return true;

      return false;
    });
  }, [receipts]);

  // Время "сканов" квитанции: receipt.marks_changed_at или максимум по продуктам
  const getReceiptScanDate = (r) => {
    if (!r) return null;
    if (r.marks_changed_at) {
      const d = new Date(r.marks_changed_at);
      if (!isNaN(d)) return d;
    }
    const prods = Array.isArray(r.products) ? r.products : [];
    const ts = prods
      .map((p) => p?.marksChangedAt ?? p?.marks_changed_at)
      .filter(Boolean)
      .map((v) => (typeof v === 'number' ? v : Date.parse(String(v))))
      .filter((n) => Number.isFinite(n));
    return ts.length ? new Date(Math.max(...ts)) : null;
  };

  // События для таймлайна (единый useMemo, без условий)
  const events = useMemo(() => {
    return receipts
      .map((r) => {
        let date = null;
        if (timelineMode === 'status') {
          if (r?.status_changed_at) {
            const d = new Date(r.status_changed_at);
            if (!isNaN(d)) date = d;
          }
        } else {
          date = getReceiptScanDate(r);
        }
        if (!date) return null;
        return {
          id: r.id,
          date,
          status: r.status,
          label: `№${r.receipt_number}`,
          address: r.address,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);
  }, [receipts, timelineMode]);

  // Позиция для подсветки на карте
  const highlightPosition = useMemo(() => {
    if (!highlightReceiptId) return null;
    const rec = receipts.find((r) => r?.id === highlightReceiptId);
    const lat = Number(rec?.latitude);
    const lng = Number(rec?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
  }, [highlightReceiptId, receipts]);

  // Рендерим ТОЛЬКО когда модалка открыта и есть маршрут
  if (!open) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>
          <FaTimes />
        </button>

        <h2 className={styles.title}>Маршрут №{route.route_number}</h2>

        {/* Инфо по маршруту */}
        <div className={styles.routeInfo}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Дата:</span>
            <span className={styles.infoValue}>
              {route?.route_date ? new Date(route.route_date).toLocaleDateString('ru-RU') : '—'}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Водитель:</span>
            <span className={styles.infoValue}>{route?.driver || '—'}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Грузчик:</span>
            <span className={styles.infoValue}>
              {route?.loader1 || '—'}
              {route?.loader2 ? `, ${route.loader2}` : ''}
            </span>
          </div>
        </div>

        {/* Статистика */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Первое изменение</div>
            <div className={styles.statValue}>{fmtDate(firstChangeTime)}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Последнее изменение</div>
            <div className={styles.statValue}>{fmtDate(lastChangeTime)}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Всего квитанций</div>
            <div className={styles.statValue}>{stats.total}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>В работе</div>
            <div className={styles.statValue}>{stats.inProgress}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Закрыто</div>
            <div className={styles.statValue}>{stats.closed}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Не доставлено</div>
            <div className={styles.statValue}>{stats.undelivered}</div>
          </div>

          <div
            className={styles.statCard}
            style={{ position: 'relative', cursor: 'pointer' }}
            onClick={() => setShowUnscannedDetails((v) => !v)}
          >
            <div className={styles.statLabel}>Товаров не отсканировано</div>
            <div className={styles.statValue}>{stats.unscannedItems}</div>
            {showUnscannedDetails && unscannedDetails.length > 0 && (
              <div className={styles.tooltip}>
                <ul className={styles.tooltipList}>
                  {unscannedDetails.map((p, i) => (
                    <li key={i}>
                      {p.name}: {p.scanned}/{p.total}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className={styles.statCard}>
            <div className={styles.statLabel}>Не пробито чеков</div>
            <div className={styles.statValue}>{stats.notChecked}</div>
          </div>
        </div>

        {/* Таймлайн + Карта */}
        <div className={styles.mainBlock}>
          <div className={styles.timelineBlock}>
            <div className={styles.timelineHeader}>
              <label>
                <input
                  type="radio"
                  name="tlmode"
                  value="status"
                  checked={timelineMode === 'status'}
                  onChange={() => setTimelineMode('status')}
                />
                &nbsp;По статусам
              </label>
              <label style={{ marginLeft: 12 }}>
                <input
                  type="radio"
                  name="tlmode"
                  value="scan"
                  checked={timelineMode === 'scan'}
                  onChange={() => setTimelineMode('scan')}
                />
                &nbsp;По сканам
              </label>
            </div>

            <div className={styles.timelineRow}>
              {events.map((e) => (
                <div
                  key={e.id}
                  className={`${styles.eventChip} ${highlightReceiptId === e.id ? styles.activeEvent : ''}`}
                  style={{ backgroundColor: STATUS_COLOR[e.status] || '#999' }}
                  title={`${e.label} — ${e.address || ''}`}
                  onClick={() => setHighlightReceiptId(e.id)}
                >
                  <span className={styles.eventLabel}>{e.label}</span>
                  <span className={styles.eventTime}>{fmtTime(e.date)}</span>
                </div>
              ))}
              {!events.length && <div className={styles.muted}>Нет событий для выбранного режима</div>}
            </div>
          </div>

          <div className={styles.mapBlock}>
            <MapComponent
              center={[route?.centerLat ?? 55.75, route?.centerLng ?? 37.61]}
              zoom={12}
              receipts={receipts}
              highlightPosition={highlightPosition}
            />
          </div>
        </div>

        {/* Проблемные квитанции */}
        <div className={styles.problemsSection}>
          <h3 className={styles.sectionTitle}>Проблемные квитанции</h3>
          {problems.length ? (
            <table className={styles.problemsTable}>
              <thead>
                <tr>
                  <th>Адрес</th>
                  <th>№</th>
                  <th>Статус</th>
                  <th>Время</th>
                  <th>Чек</th>
                </tr>
              </thead>
              <tbody>
                {problems.map((r) => (
                  <tr key={r.id}>
                    <td className={styles.colAddr} title={r.address}>{r.address}</td>
                    <td className={styles.colNumber}>#{r.receipt_number}</td>
                    <td className={styles.colStatus}>{r.status}</td>
                    <td className={styles.colTime}>{fmtDate(r?.status_changed_at ? new Date(r.status_changed_at) : null)}</td>
                    <td className={r.is_check_issued ? styles.checkedOk : styles.checkedFail}>
                      {r.is_check_issued ? '✓' : '✗'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.noProblems}>Нет проблемных квитанций 🎉</div>
          )}
        </div>
      </div>
    </div>
  );
}
